import { GALLERY_TEMPLATE_RIGHT, PREFERENCE_DEFAULT } from "csi/constants";
import { isDefined } from "csi/utility/coreUtility";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { ChangeEvent, FunctionComponent } from "react";
import { ImageSize } from "src/c8-platform/type/image";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ICustomViewGalleryOptions, IGalleryViewDropDownOptions, IMAGE_DIMENSION_MIN } from "./CustomViewOptions";
import { CustomViewQueryOptionsStore } from "./CustomViewQueryOptionsStore";

const GALLERY_WIDTH_MIN = 150;
const PRINT_OPTIONS_ROWS_PER_PAGE_MIN = 1;
const GROUP_HEADER_WIDTH_MIN = 25;

const isValidNumber = (val: number | string | null): boolean => {
	const numVal = parseInt(val?.toString());

	return !isNaN(numVal) && numVal >= 0;
};

const isValidNumberWithMin = (val: number | string, min: number): boolean => {
	const numVal = parseInt(val?.toString());

	return isValidNumber(numVal) && numVal >= min;
};

/**
 * This store contains information about gallery view
 */
class GalleryCustomViewOptionsStore extends CustomViewQueryOptionsStore<ICustomViewGalleryOptions> {
	readonly galleryDropDownOptions: IGalleryViewDropDownOptions;

	@observable private rawGalleryAttributeWidth: number | string | null = null;

	@observable private rawGalleryImageWidth: number | string = IMAGE_DIMENSION_MIN;

	@observable private rawGalleryImageHeight: number | string = IMAGE_DIMENSION_MIN;

	@observable private rawGalleryRowsPerPage: number | string | null = null;

	@observable private rawGroupHeaderWidth: number | string | null = null;

	constructor(
		galleryDropDownOptions: IGalleryViewDropDownOptions,
		component: FunctionComponent,
		customViewDialogStore: CustomViewDialogStore,
		options?: ICustomViewGalleryOptions,
	) {
		super(galleryDropDownOptions, component, customViewDialogStore, options);
		makeObservable(this);
		this.galleryDropDownOptions = galleryDropDownOptions;

		this.initRawAttributes();

		this.trackReaction(reaction(
			() => this.options,
			() => {
				this.initRawAttributes();
			},
		));
	}

	private initRawAttributes = (): void => {
		this.rawGalleryAttributeWidth = this.options.galleryAttributeWidth || null;
		this.rawGalleryImageWidth = this.options.galleryImageWidth || IMAGE_DIMENSION_MIN;
		this.rawGalleryImageHeight = this.options.galleryImageHeight || IMAGE_DIMENSION_MIN;
		this.rawGalleryRowsPerPage = this.options.galleryRowsPerPage || null;
		this.rawGroupHeaderWidth = this.options.groupHeaderWidth || null;
	};
	
	/** indicates whether to disable the editing of query size, true when column/row attribute group is selected */
	get disableQuerySize(): boolean {
		const { dataManagementOptions: { isGalleryView }, groupingStore } = this.customViewDialogStore.dataManagementStore;
		return (groupingStore?.currentGroupSettings?.key1 || groupingStore?.currentGroupSettings?.key2) && isGalleryView;
	}

	get attributeWidthRangeMsg(): string {
		return this.customViewDialogStore.loc.resource("ViewPreferences_MinimumValueError", [GALLERY_WIDTH_MIN]);
	}

	get imageDimensionRangeMsg(): string {
		return this.customViewDialogStore.loc.resource("ViewPreferences_MinimumValueError", [IMAGE_DIMENSION_MIN]);
	}

	get printOptionRowsPerPageRangeMsg(): string {
		return this.customViewDialogStore.loc.resource("ViewPreferences_MinimumValueError", [PRINT_OPTIONS_ROWS_PER_PAGE_MIN]);
	}

	get groupHeaderWidthRangeMsg(): string {
		return this.customViewDialogStore.loc.resource("ViewPreferences_MinimumValueError", [GROUP_HEADER_WIDTH_MIN]);
	}

	get displayGalleryAttributeWidth(): string {
		return this.rawGalleryAttributeWidth?.toString() || "";
	}

	get displayGalleryImageWidth(): string {
		return this.rawGalleryImageWidth?.toString() || "";
	}

	get displayGalleryImageHeight(): string {
		return this.rawGalleryImageHeight?.toString() || "";
	}

	get displayGalleryRowsPerPage(): string {
		return this.rawGalleryRowsPerPage?.toString() || "";
	}

	get displayGroupHeaderWidth(): string {
		return this.isGroupHeaderWidthDisabled ? "" : this.rawGroupHeaderWidth?.toString() || "";
	}

	get galleryAttributeWidth(): number {
		return this.options.galleryAttributeWidth || 0;
	}

	get galleryImageWidth(): number {
		return this.options.galleryImageWidth || IMAGE_DIMENSION_MIN;
	}

	get galleryImageHeight(): number {
		return this.options.galleryImageHeight || IMAGE_DIMENSION_MIN;
	}

	get galleryRowsPerPage(): number {
		return this.options.galleryRowsPerPage || 0;
	}

	get groupHeaderWidth(): number {
		return this.options.groupHeaderWidth || 0;
	}

	get isGalleryAttributeWidthDefault(): boolean {
		return this.rawGalleryAttributeWidth === null;
	}

	get isGalleryRowsPerPageDefault(): boolean {
		return this.rawGalleryRowsPerPage === null;
	}

	get isGroupHeaderWidthDefault(): boolean {
		return this.rawGroupHeaderWidth === null;
	}

	get galleryAttributeWidthValidationMsg(): string {
		if (this.isGalleryAttributeWidthDefault) {
			return "";
		}

		if (!isValidNumber(this.rawGalleryAttributeWidth)) {
			return this.customViewDialogStore.loc.resource("ValidateNotValid");
		}

		return this.attributeWidthRangeMsg;
	}

	get galleryImageWidthValidationMsg(): string {
		if (!isValidNumber(this.rawGalleryImageWidth)) {
			return this.customViewDialogStore.loc.resource("ValidateNotValid");
		}

		return this.imageDimensionRangeMsg;
	}

	get galleryImageHeightValidationMsg(): string {
		if (!isValidNumber(this.rawGalleryImageHeight)) {
			return this.customViewDialogStore.loc.resource("ValidateNotValid");
		}

		return this.imageDimensionRangeMsg;
	}

	get galleryRowsPerPageValidationMsg(): string {
		if (this.isGalleryRowsPerPageDefault) {
			return "";
		}

		if (!isValidNumber(this.rawGalleryRowsPerPage)) {
			return this.customViewDialogStore.loc.resource("ValidateNotValid");
		}

		return this.printOptionRowsPerPageRangeMsg;
	}

	get groupHeaderWidthValidationMsg(): string {
		if (!this.showGroupHeaderValidation) {
			return "";
		}

		if (!isValidNumber(this.rawGroupHeaderWidth)) {
			return this.customViewDialogStore.loc.resource("ValidateNotValid");
		}

		return this.groupHeaderWidthRangeMsg;
	}

	get hasGalleryAttributeWidthError(): boolean {
		return !this.isGalleryAttributeWidthDefault && !isValidNumberWithMin(this.rawGalleryAttributeWidth, GALLERY_WIDTH_MIN);
	}

	get hasGalleryImageWidthError(): boolean {
		return !isValidNumberWithMin(this.rawGalleryImageWidth, IMAGE_DIMENSION_MIN);
	}

	get hasGalleryImageHeightError(): boolean {
		return !isValidNumberWithMin(this.rawGalleryImageHeight, IMAGE_DIMENSION_MIN);
	}

	get hasGalleryRowsPerPageValidationError(): boolean {
		return !this.isGalleryRowsPerPageDefault && !isValidNumberWithMin(this.rawGalleryRowsPerPage, PRINT_OPTIONS_ROWS_PER_PAGE_MIN);
	}

	get hasGroupHeaderWidthError(): boolean {
		return this.showGroupHeaderValidation &&
			!isValidNumberWithMin(this.rawGroupHeaderWidth, GROUP_HEADER_WIDTH_MIN);
	}

	get isGroupHeaderWidthDisabled(): boolean {
		return !this.customViewDialogStore.dataManagementStore.groupingStore?.isGalleryGrouping;
	}

	get showGroupHeaderValidation(): boolean {
		return !this.isGroupHeaderWidthDefault && !this.isGroupHeaderWidthDisabled;
	}

	/** default value of the Rows per Page field */
	get printOptionRowsPerPageTextDefaultValue(): string {
		const { options } = this;
		const { galleryRowsPerPage } = options as ICustomViewGalleryOptions;
		return isDefined(galleryRowsPerPage) && (galleryRowsPerPage !== 0) ? galleryRowsPerPage.toString() : PREFERENCE_DEFAULT;
	}

	/** indicates whether gallery rows per page option should be shown */
	get showGalleryRowsPerPage(): boolean {
		return !!this.options.galleryAttributeWidth && !this.hasGalleryAttributeWidthError;
	}

	@action onChangeAttributeWidth = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		if (event.target.value === "") {
			this.rawGalleryAttributeWidth = null;
			this.options.galleryAttributeWidth = 0;

			return;
		}

		this.rawGalleryAttributeWidth = event.target.value;

		const value = parseFloat(event.target.value);

		if (isValidNumberWithMin(value, GALLERY_WIDTH_MIN)) {
			this.options.galleryAttributeWidth = value;
		}
	};

	@action setPrintOptionRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
		if (event.target.value === "") {
			this.rawGalleryRowsPerPage = null;
			this.options.galleryRowsPerPage = 0;

			return;
		}

		this.rawGalleryRowsPerPage = event.target.value;

		const value = parseFloat(event.target.value);

		if (isValidNumberWithMin(value, PRINT_OPTIONS_ROWS_PER_PAGE_MIN)) {
			this.options.galleryRowsPerPage = value;
		}
	};

	@action onChangeGroupHeaderWidth = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		if (event.target.value === "") {
			this.rawGroupHeaderWidth = null;
			this.options.groupHeaderWidth = 0;

			return;
		}

		this.rawGroupHeaderWidth = event.target.value;
		
		const value = parseFloat(event.target.value);

		if (isValidNumberWithMin(value, GROUP_HEADER_WIDTH_MIN)) {
			this.options.groupHeaderWidth = value;
		}
	};

	/** set the value for group items per row */
	@action setGroupItemsPerRow = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.groupItemsPerRow = value.id;
	};

	/** set the value for image label */
	@action setImageLabel = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.galleryImageLabel = (value.id === PREFERENCE_DEFAULT) ? undefined : value.id;
	};

	/** set the value for card template */
	@action setCardTemplate = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.galleryTemplate = value.id;
		if (value.id !== GALLERY_TEMPLATE_RIGHT) {
			this.options.galleryAttributeWidth = this.options.galleryImageWidth;
		}
	};

	/** set the value for imageSize */
	@action setImageSize = (event: ChangeEvent<HTMLInputElement>, value: ComponentItem): void => {
		this.options.imageSize = value.id as ImageSize;
		if (value.id === ImageSize.IMAGE_SIZE_CUSTOM) {
			// when user switches from IMAGE_SIZE_CUSTOM to
			// another option and then back to IMAGE_SIZE_CUSTOM,
			// we need to update the raw values back to the valid ones
			if (this.options.galleryImageWidth) {
				this.rawGalleryImageWidth = this.options.galleryImageWidth || IMAGE_DIMENSION_MIN;
			}
			if (this.options.galleryImageHeight) {
				this.rawGalleryImageHeight = this.options.galleryImageHeight || IMAGE_DIMENSION_MIN;
			}
			
			if (this.options.galleryTemplate !== GALLERY_TEMPLATE_RIGHT) {
				this.options.galleryAttributeWidth = this.options.galleryImageWidth || IMAGE_DIMENSION_MIN;
			}
		}
		else {
			this.options.galleryImageWidth = 0;
			this.options.galleryImageHeight = 0;
			this.options.galleryAttributeWidth = 0;
		}
	};

	@action setImageWidth = (event: ChangeEvent<HTMLInputElement>): void => {
		this.rawGalleryImageWidth = event.target.value;

		const value = parseFloat(event.target.value);

		if (isValidNumberWithMin(value, IMAGE_DIMENSION_MIN)) {
			this.options.galleryImageWidth = value;

			if (this.options.galleryTemplate !== GALLERY_TEMPLATE_RIGHT) {
				this.options.galleryAttributeWidth = value;
			}
		}
	};

	@action setImageHeight = (event: ChangeEvent<HTMLInputElement>): void => {
		this.rawGalleryImageHeight = event.target.value;

		const value = parseFloat(event.target.value);

		if (isValidNumberWithMin(value, IMAGE_DIMENSION_MIN)) {
			this.options.galleryImageHeight = value;
		}
	};

	/** set the value for query size */
	@action setQuerySize = (event: ChangeEvent<{}>, item: ComponentItem): void => {
		this.options.querySize = item.value;
	};

	@action setGroupHeaderStyle = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.groupHeaderStyle = value.id;
	};

	@computed get selectedQuerySize(): ComponentItem {
		const { querySize } = this.options;
		return this.galleryDropDownOptions.resultCounts.find((item) => item.value === querySize);
	}

	@computed get selectedImageSizes(): ComponentItem {
		const { imageSize } = this.options;
		return this.galleryDropDownOptions.imageSizes.find((item) => item.id === imageSize);
	}

	@computed get selectedGalleryTemplate(): ComponentItem | undefined {
		const { galleryTemplate } = this.options;
		return this.galleryDropDownOptions.cardTemplates?.find((item) => item.id === galleryTemplate);
	}

	@computed get selectedGalleryImageLabel(): ComponentItem {
		const { galleryImageLabel } = this.options;
		return this.galleryDropDownOptions.imageLabels.find((item) => item.id === (galleryImageLabel || PREFERENCE_DEFAULT));
	}

	@computed get selectedGroupHeaderStyle(): ComponentItem {
		const { groupHeaderStyle } = this.options;
		return this.galleryDropDownOptions.groupHeaderStyleOptions.find((item) => item.id === ((groupHeaderStyle === "") ? PREFERENCE_DEFAULT : groupHeaderStyle));
	}

	@computed get selectedGroupItemsPerRow(): ComponentItem {
		const { groupItemsPerRow } = this.options;
		return this.galleryDropDownOptions.groupItemsPerRowOptions.find((item) => item.id === groupItemsPerRow?.toString());
	}
}

export = GalleryCustomViewOptionsStore;